import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import { apiClient } from "../api/client";
import { RootState } from "../store";
import { IFeeQuerySaveRequest, IFeeInformationFlyerViewModel } from "../types/feeInformationFlyer";

interface UseFeeInformationFlyerReturn {
  data: IFeeInformationFlyerViewModel | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// UI-side configuration logic that was moved from the backend
const calculateConfigurationDates = (effectiveFromDate?: string) => {
  // Default effective date to start of current year if not provided
  const effectiveDate = effectiveFromDate ? dayjs(effectiveFromDate) : dayjs().startOf("year");

  // Calculate principal response due date (28 days before effective date)
  const principalResponseDueDate = effectiveDate.subtract(28, "days");

  // Calculate parent communication date (21 days before effective date)
  const parentCommunicationDate = effectiveDate.subtract(21, "days");

  // Determine if we should show term information
  const showTermInEffectiveFrom = true; // Default to true as per backend logic

  // Use last year CCS for current fees flag
  const useLastYearCcsForCurrent = false; // Default to false as per backend logic

  return {
    principalResponseDueDate,
    parentCommunicationDate,
    showTermInEffectiveFrom,
    effectiveFromDate: effectiveDate,
    useLastYearCcsForCurrent,
  };
};

// Fee query permission logic moved to UI
const calculateFeeQueryPermissions = (serviceInformationFlyer: any, principalResponseDueDate: dayjs.Dayjs) => {
  if (!serviceInformationFlyer.proposedFees) {
    return false;
  }

  const isBeforeDeadline = dayjs().isBefore(principalResponseDueDate);

  return serviceInformationFlyer.proposedFees.allowFeeQuery && isBeforeDeadline;
};

export const useFeeInformationFlyer = (dateAs?: string): UseFeeInformationFlyerReturn => {
  const selectedService = useSelector((state: RootState) => state.service.selectedService);

  const {
    data: rawData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["feeInformationFlyer", selectedService?.serviceId, dateAs],
    queryFn: async () => {
      if (!selectedService?.serviceId) {
        throw new Error("No service selected");
      }
      const dateParam = dateAs ? dayjs(dateAs) : null;
      const result = await apiClient.feeInformationFlyer.getFeeInformationFlyer(selectedService.serviceId, dateParam);
      return result as unknown as IFeeInformationFlyerViewModel;
    },
    enabled: !!selectedService?.serviceId,
    staleTime: 0, // Always fetch fresh data for fee information
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Process the raw data and add client-side calculated values
  const data: IFeeInformationFlyerViewModel | null = rawData
    ? {
        ...rawData,
        // Add the configuration dates calculated on the client side
        // Convert dayjs dates to ISO strings for calculation function
        ...calculateConfigurationDates(rawData.effectiveFromDate?.toISOString() ?? undefined),
        // Update the allowQuery flag with client-side logic
        serviceInformationFlyer: {
          ...rawData.serviceInformationFlyer,
          allowQuery: calculateFeeQueryPermissions(
            rawData.serviceInformationFlyer,
            calculateConfigurationDates(rawData.effectiveFromDate?.toISOString() ?? undefined).principalResponseDueDate,
          ),
        },
      }
    : null;

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

interface UseFboFeeInformationFlyerReturn {
  data: any;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useFboFeeInformationFlyer = (dateAs?: string): UseFboFeeInformationFlyerReturn => {
  const selectedService = useSelector((state: RootState) => state.service.selectedService);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["fboFeeInformationFlyer", selectedService?.serviceId, dateAs],
    queryFn: async () => {
      if (!selectedService?.serviceId) {
        throw new Error("No service selected");
      }
      const dateParam = dateAs ? dayjs(dateAs) : null;
      return await apiClient.feeInformationFlyer.getFboFeeInformationFlyer(selectedService.serviceId, dateParam);
    },
    enabled: !!selectedService?.serviceId,
    staleTime: 0, // Always fetch fresh data for fee information
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

interface UseFeeQueryMutationReturn {
  saveFeeQuery: (serviceId: number, request: IFeeQuerySaveRequest) => Promise<any>;
  processFeeAction: (serviceId: number, feeAction: string, callback?: boolean) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

export const useFeeQueryMutation = (): UseFeeQueryMutationReturn => {
  const queryClient = useQueryClient();

  const saveFeeQueryMutation = useMutation({
    mutationFn: ({ serviceId, request }: { serviceId: number; request: IFeeQuerySaveRequest }) =>
      apiClient.feeInformationFlyer.saveFeeQuery(serviceId, request as any),
    onSuccess: () => {
      // Invalidate and refetch fee information after successful save
      queryClient.invalidateQueries({ queryKey: ["feeInformationFlyer"] });
      queryClient.invalidateQueries({ queryKey: ["fboFeeInformationFlyer"] });
      // Invalidate pending fee review to update the notification icon
      queryClient.invalidateQueries({ queryKey: ["pendingFeeReview"] });
    },
  });

  const processFeeActionMutation = useMutation({
    mutationFn: () => {
      // Note: The processFeeAction method doesn't exist in the generated client yet
      // This will need to be implemented or removed
      return Promise.resolve();
    },
    onSuccess: () => {
      // Invalidate and refetch fee information after successful action
      queryClient.invalidateQueries({ queryKey: ["feeInformationFlyer"] });
      queryClient.invalidateQueries({ queryKey: ["fboFeeInformationFlyer"] });
      // Invalidate pending fee review to update the notification icon
      queryClient.invalidateQueries({ queryKey: ["pendingFeeReview"] });
    },
  });

  return {
    saveFeeQuery: (serviceId: number, request: IFeeQuerySaveRequest) =>
      saveFeeQueryMutation.mutateAsync({ serviceId, request }),
    processFeeAction: () => processFeeActionMutation.mutateAsync(),
    isLoading: saveFeeQueryMutation.isPending || processFeeActionMutation.isPending,
    error: (saveFeeQueryMutation.error || processFeeActionMutation.error) as Error | null,
  };
};

interface UsePendingFeeReviewReturn {
  hasPendingFees: boolean;
  isLoading: boolean;
  error: Error | null;
}

export const usePendingFeeReview = (): UsePendingFeeReviewReturn => {
  const selectedService = useSelector((state: RootState) => state.service.selectedService);

  const { data, isLoading, error } = useQuery({
    queryKey: ["pendingFeeReview", selectedService?.serviceId],
    queryFn: async () => {
      if (!selectedService?.serviceId) {
        return { hasPendingFees: false, serviceId: null };
      }
      return await apiClient.feeInformationFlyer.checkPendingFeeReview(selectedService.serviceId);
    },
    enabled: !!selectedService?.serviceId,
    staleTime: 30 * 1000, // Cache for 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  return {
    hasPendingFees: data?.hasPendingFees ?? false,
    isLoading,
    error,
  };
};
